import React, { useEffect, ReactNode } from "react";
import { useNotificationSummary } from "../hooks/useNotificationSummary";
import { useUnifiedNotificationStore } from "../stores/unifiedNotificationStore";
import { useHotelHub } from "../providers/signalr";
import { logger } from "../utils/logger";

interface UnifiedNotificationProviderProps {
  children: ReactNode;
}

export const UnifiedNotificationProvider: React.FC<
  UnifiedNotificationProviderProps
> = ({ children }) => {
  const { on, off, isConnected } = useHotelHub();
  const { updateFromSignalR, reset } = useUnifiedNotificationStore();

  // Initialize React Query hook for fetching summary
  useNotificationSummary();

  useEffect(() => {
    if (!isConnected) {
      logger.log(
        "UnifiedNotificationProvider: NotificationHub not connected yet",
      );
      return;
    }

    logger.log(
      "UnifiedNotificationProvider: Registering SignalR event handlers",
    );

    // Food Orders
    const handleOrderCountUpdate = (newCount: number) => {
      logger.log(
        "UnifiedNotificationProvider: ReceiveOrderCountUpdate",
        newCount,
      );
      updateFromSignalR("orders", newCount);
    };

    // Late Checkouts
    const handleLateCheckoutCountUpdate = (newCount: number) => {
      logger.log(
        "UnifiedNotificationProvider: ReceiveLateCheckoutCountUpdate",
        newCount,
      );
      updateFromSignalR("lateCheckouts", newCount);
    };

    // Service Requests
    const handleServiceRequestCountUpdate = (newCount: number) => {
      logger.log(
        "UnifiedNotificationProvider: ReceiveServiceRequestCountUpdate",
        newCount,
      );
      updateFromSignalR("serviceRequests", newCount);
    };

    // Unread Chats
    const handleUnreadChatCountUpdate = (newCount: number) => {
      logger.log(
        "UnifiedNotificationProvider: ReceiveUnreadChatCountUpdate",
        newCount,
      );
      updateFromSignalR("chats", newCount);
    };

    // Transfer Requests
    const handleTransferRequestCountUpdate = (newCount: number) => {
      logger.log(
        "UnifiedNotificationProvider: ReceiveTransferRequestCountUpdate",
        newCount,
      );
      updateFromSignalR("transferRequests", newCount);
    };

    // Activity bookings
    const handleActivityBookingCountUpdate = (newCount: number) => {
      logger.log(
        "UnifiedNotificationProvider: ReceiveActivityBookingCountUpdate",
        newCount,
      );
      updateFromSignalR("activityBookings", newCount);
    };

    // Check-ins
    const handleCheckinCountUpdate = (newCount: number) => {
      logger.log(
        "UnifiedNotificationProvider: ReceiveCheckinCountUpdate",
        newCount,
      );
      updateFromSignalR("checkins", newCount);
    };

    // Queue Items - custom handler for this since it might work differently
    const handleQueueItemUpdate = () => {
      logger.log("UnifiedNotificationProvider: ReceiveQueueItemUpdate");
      // For queue updates, we might need to refetch the full count
      // This is a simplified version - you might want to enhance this
      // based on your queue update logic
    };

    // Register all handlers
    on("ReceiveOrderCountUpdate", handleOrderCountUpdate);
    on("ReceiveLateCheckoutCountUpdate", handleLateCheckoutCountUpdate);
    on("ReceiveServiceRequestCountUpdate", handleServiceRequestCountUpdate);
    on("ReceiveUnreadChatCountUpdate", handleUnreadChatCountUpdate);
    on("ReceiveTransferRequestCountUpdate", handleTransferRequestCountUpdate);
    on("ReceiveActivityBookingCountUpdate", handleActivityBookingCountUpdate);
    on("ReceiveCheckinCountUpdate", handleCheckinCountUpdate);
    on("ReceiveQueueItemUpdate", handleQueueItemUpdate);

    logger.log(
      "UnifiedNotificationProvider: All SignalR event handlers registered",
    );

    // Cleanup function to remove handlers
    return () => {
      logger.log(
        "UnifiedNotificationProvider: Cleaning up SignalR event handlers",
      );
      off("ReceiveOrderCountUpdate", handleOrderCountUpdate);
      off("ReceiveLateCheckoutCountUpdate", handleLateCheckoutCountUpdate);
      off("ReceiveServiceRequestCountUpdate", handleServiceRequestCountUpdate);
      off("ReceiveUnreadChatCountUpdate", handleUnreadChatCountUpdate);
      off(
        "ReceiveTransferRequestCountUpdate",
        handleTransferRequestCountUpdate,
      );
      off(
        "ReceiveActivityBookingCountUpdate",
        handleActivityBookingCountUpdate,
      );
      off("ReceiveCheckinCountUpdate", handleCheckinCountUpdate);
      off("ReceiveQueueItemUpdate", handleQueueItemUpdate);
    };
  }, [isConnected, on, off, updateFromSignalR]);

  // Reset store on unmount or logout
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  return <>{children}</>;
};
